import { useMemo, useState } from "react";
import { Box, Flex, Text } from "@radix-ui/themes";
import DataGrid, { DataGridColumn } from "@/components/ui/DataGrid";
import { DashboardWidget } from "@/types/breeze/Dashboard";
import { MaterialisedViewData } from "@/types/breeze/MaterialisedView";
import { PaginatedResult } from "@/types/breeze/Any";

type DeltaTone = "gray" | "green" | "red";

function deltaTone(value: number | null | undefined): { tone: DeltaTone; arrow: string } {
  if (value === null || value === undefined) return { tone: "gray", arrow: "" };
  if (Math.abs(value) < 0.01) return { tone: "gray", arrow: "—" };
  return value >= 0 ? { tone: "green", arrow: "▲" } : { tone: "red", arrow: "▼" };
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

function looksLikeDate(v: unknown): v is string {
  return typeof v === "string" && ISO_DATE_RE.test(v);
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const hasTime = iso.includes("T") || iso.includes(" ");
  try {
    return hasTime
      ? new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(d)
      : new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "short", year: "numeric" }).format(d);
  } catch {
    return iso;
  }
}

interface Props {
  widget: DashboardWidget;
  data?: MaterialisedViewData;
  loading?: boolean;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

interface PivotRow extends Record<string, unknown> {
  id: number;
}

const TOTALS_ROW_ID = -1;
const LEADING_FIELD_PREFERENCES = ["name", "title", "email", "username", "first_name", "last_name"];
const CURRENCY_FIELDS = new Set(["amount", "net_amount", "net_revenue", "mrr"]);
const CURRENCY_SOURCES = new Set(["payments", "subscriptions"]);
const DEFAULT_CURRENCY = "GBP";

function isMonetaryColumn(col: { source_kind?: string | null; value_field?: string | null }): boolean {
  return !!col.source_kind && CURRENCY_SOURCES.has(col.source_kind) &&
    !!col.value_field && CURRENCY_FIELDS.has(col.value_field);
}

function formatCurrency(v: unknown, currency: string): string {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return String(v ?? "");
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(n);
  } catch {
    return `${n.toFixed(2)}`;
  }
}

function pickLeadingFields(sampleRow: Record<string, unknown> | undefined): string[] {
  if (!sampleRow) return [];
  const keys = Object.keys(sampleRow);
  const picked = LEADING_FIELD_PREFERENCES.filter((k) => keys.includes(k));
  return picked.length > 0 ? picked : keys.includes("id") ? ["id"] : keys.slice(0, 1);
}

export function MaterialisedViewWidget({
  widget, data, loading,
  page: pageProp,
  pageSize: pageSizeProp,
  onPageChange,
  onPageSizeChange,
}: Props) {
  // Local fallback state — used when the parent doesn't manage pagination (dashboard grid today).
  const [localPage, setLocalPage] = useState(1);
  const [localPageSize, setLocalPageSize] = useState(50);
  const page = pageProp ?? localPage;
  const pageSize = pageSizeProp ?? localPageSize;
  const setPage = onPageChange ?? setLocalPage;
  const setPageSize = onPageSizeChange ?? setLocalPageSize;

  const leadingFields = useMemo(() => {
    const firstDataRow = data?.rows?.[0]?.base_row;
    return pickLeadingFields(firstDataRow);
  }, [data?.rows]);

  const deltaByColumn = data?.delta_pct ?? null;

  const columns = useMemo<DataGridColumn<PivotRow>[]>(() => {
    if (!data?.view) return [];
    const pivotCols = data.view.columns ?? [];
    const leading: DataGridColumn<PivotRow>[] = leadingFields.map((field) => ({
      field,
      header: field.replace(/_/g, " "),
      minWidth: 140,
      pinned: "left" as const,
      sortable: true,
      filter: true,
      valueGetter: (row) => {
        const v = row[field];
        if (v === null || v === undefined) return "";
        return String(v);
      },
    }));
    const pivot: DataGridColumn<PivotRow>[] = pivotCols.map((col) => {
      const delta = deltaByColumn?.[col.id];
      const monetary = isMonetaryColumn(col);
      const currency = (col.currency || DEFAULT_CURRENCY).toUpperCase();
      const format = (v: unknown): string => {
        if (v === null || v === undefined || v === "") return "";
        if (monetary) return formatCurrency(v, currency);
        if (looksLikeDate(v)) return formatDate(v);
        return String(v);
      };
      const deltaBadge = delta === undefined || delta === null ? undefined : (() => {
        const { tone, arrow } = deltaTone(delta);
        return (
          <Text size="1" weight="medium" color={tone}>
            {arrow} {Math.abs(delta).toFixed(1)}%
          </Text>
        );
      })();
      return {
        field: col.id,
        header: col.label,
        headerBadge: deltaBadge,
        minWidth: 120,
        flex: 1,
        sortable: true,
        filter: true,
        cellRenderer: (row: PivotRow) => format(row[col.id]),
        valueGetter: (row) => format(row[col.id]),
      };
    });
    return [...leading, ...pivot];
  }, [data?.view?.columns, leadingFields, deltaByColumn]);

  const totalsRow = useMemo<PivotRow | null>(() => {
    if (!data) return null;
    const totalsLeading: Record<string, unknown> = {};
    if (leadingFields.length > 0) totalsLeading[leadingFields[0]] = "TOTAL";
    return { id: TOTALS_ROW_ID, ...totalsLeading, ...(data.totals ?? {}) };
  }, [data, leadingFields]);

  const pagedResult = useMemo<PaginatedResult<PivotRow>>(() => {
    if (!data) {
      return {
        items: [],
        total_items: 0,
        total_pages: 0,
        page: 1,
        page_size: pageSize,
        has_next_page: false,
        has_previous_page: false,
      };
    }
    // Rows come pre-paged from the server. base_row_count is the tenant-wide total; total_pages is server-computed.
    const dataRows: PivotRow[] = (data.rows ?? []).map((r) => ({
      id: r.row_id,
      ...(r.base_row ?? {}),
      ...(r.cells ?? {}),
    }));
    const totalItems = data.view?.base_row_count ?? dataRows.length;
    const totalPages = data.view?.total_pages ?? Math.max(1, Math.ceil(totalItems / pageSize));
    return {
      items: dataRows,
      total_items: totalItems,
      total_pages: totalPages,
      page,
      page_size: pageSize,
      has_next_page: page < totalPages,
      has_previous_page: page > 1,
    };
  }, [data, page, pageSize]);

  return (
    <Box p="3" style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Flex align="center" gap="3" mb="2">
        <Text size="1" color="gray" asChild>
          <div>{widget.title}</div>
        </Text>
      </Flex>
      {loading && <Text size="2" color="gray">Loading…</Text>}
      {!loading && data?.error && <Text size="2" color="red">{data.error}</Text>}
      {!loading && !data?.error && columns.length === 0 && (
        <Text size="2" color="gray">
          {data ? "View has no columns configured yet." : "No data returned."}
        </Text>
      )}
      {!loading && !data?.error && columns.length > 0 && (
        <Box style={{ flex: 1, minHeight: 0 }}>
          <Flex direction="column" style={{ height: "100%" }}>
            <DataGrid<PivotRow>
              data={pagedResult}
              columns={columns}
              storageKey={`materialised_view_widget_${widget.id ?? "preview"}`}
              currentPage={page}
              currentPageSize={pageSize}
              rowSelection="none"
              height="100%"
              domLayout="normal"
              pinnedTopRows={totalsRow ? [totalsRow] : undefined}
              showPagination={pagedResult.total_items > pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </Flex>
        </Box>
      )}
      {!loading && data?.diagnostics && Object.keys(data.diagnostics).length > 0 && (
        <Box mt="2">
          {Object.entries(data.diagnostics).map(([colId, msg]) => {
            const label = data.view.columns.find((c) => c.id === colId)?.label ?? colId;
            return (
              <Text as="div" size="1" color="gray" key={colId}>
                {label}: {msg}
              </Text>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
